import { useEffect, useState } from 'react'
import { Navigate, useSearchParams } from 'react-router-dom'
import { useLocaleSync, useUiStrings } from '@/hooks/useUiStrings'
import { useStore } from '@/store/useStore'
import { mintRefreshToken } from '@/services/auth'
import { storeSessionTokens } from '@/services/session'

export default function AuthCallbackPage() {
  const { t } = useUiStrings()
  useLocaleSync()
  const [searchParams] = useSearchParams()
  const setSession = useStore((s) => s.setSession)
  const isAuthenticated = useStore((s) => s.isAuthenticated)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const oauthError = searchParams.get('error')
    if (oauthError) {
      setError(decodeURIComponent(oauthError))
      return
    }

    const accessToken = searchParams.get('accessToken')
    const email = searchParams.get('email')
    const name = searchParams.get('name')
    const role = searchParams.get('role')
    const authProvider = searchParams.get('authProvider') ?? undefined

    if (!accessToken || !email || !name || !role) {
      setError(t.loginInfoIncomplete)
      return
    }

    setSession({ email, displayName: name, plan: role, role, authProvider }, accessToken)

    // A round trip rather than another query parameter: see `mintRefreshToken`.
    // The session is already usable, so nothing here waits on it or fails.
    void mintRefreshToken().then((refreshToken) => {
      if (refreshToken) storeSessionTokens(accessToken, refreshToken)
    })
  }, [searchParams, setSession, t.loginInfoIncomplete])

  if (isAuthenticated) return <Navigate to="/home" replace />

  if (error) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-[var(--bg-primary)] p-4">
        <div className="max-w-md rounded-2xl border border-[var(--border-color)] bg-[var(--bg-secondary)] p-6 text-center">
          <p className="mb-2 text-sm font-semibold text-[var(--error)]">{t.loginFailed}</p>
          <p className="text-sm text-[var(--text-secondary)]">{error}</p>
          <a
            href="/login"
            className="mt-4 inline-block text-sm font-semibold text-[var(--text-primary)] hover:underline"
          >
            {t.backToLogin}
          </a>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-[var(--bg-primary)]">
      <p className="text-sm text-[var(--text-secondary)]">{t.processingLogin}</p>
    </div>
  )
}
